import { pickReplacementSpace } from '../lib/spaceSelection'
import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  MoreHorizontal,
  Plus,
  Star,
  Pencil,
  PlusCircle,
  Palette,
  ListChecks,
  Lock,
  Users,
  Copy,
  Archive,
  Trash2,
  Share2,
  ChevronRight,
  Check,
  Folder,
} from 'lucide-react'
import { Modal } from './ui/Modal'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { Button } from './ui/Button'
import { TextField } from './ui/Field'
import { TaskStatusEditor } from './TaskStatusEditor'
import { ListCreateDialog } from './ListCreateDialog'
import { DocumentCreateDialog } from './DocumentCreateDialog'
import { FolderCreateDialog } from './FolderCreateDialog'
import { api, extractApiError, isPlanLimitError } from '../lib/api'
import { useAuthStore } from '../stores/authStore'
import { useFavoriteSpaces, useHiddenSpaces } from '../lib/space-prefs'
import { CONTAINS_LETTER } from '../lib/validators'
import {
  DEFAULT_SPACE_TASK_STATUSES,
} from '../lib/types'

const SPACE_COLOURS = [
  '#0EA5E9',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#A855F7',
  '#EC4899',
  '#3B82F6',
  '#14B8A6',
  '#F97316',
  '#94A3B8',
]

const renameSchema = z.object({
  name: z
    .string({ message: 'Space Name is required.' })
    .trim()
    .min(1, 'Space Name is required.')
    .min(2, 'Space Name must be at least 2 characters.')
    .max(100, 'Space Name must be at most 100 characters.')
    .regex(CONTAINS_LETTER, 'Space Name must contain at least one letter.'),
})

export function SpaceRowActions({ space, workspaceId, onShare }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const userId = useAuthStore((s) => s.user?.id)
  const userRole = useAuthStore((s) => s.user?.role)
  const canManageWorkflow = userRole === 'company_admin'
  const hasWorkspacesManage = useAuthStore((s) =>
    s.hasSubAdminOperation('workspaces.manage'),
  )
  // Mirror the backend gate (assertCanUpdateSpace / assertCanDeleteSpace): only
  // the Space owner, a company/sub-admin, or a `full`-access member may rename,
  // recolour, flip privacy, archive or delete a Space. Hide those owner-only
  // actions for everyone else so a non-owner can't open a form that only 403s
  // on submit ("Only the Space owner can update this Space."). Delete is
  // stricter — no `full` bypass.
  const isAdminActor =
    userRole === 'company_admin' ||
    userRole === 'super_admin' ||
    hasWorkspacesManage
  const isSpaceOwner = String(space.createdById ?? '') === String(userId ?? '')
  const canUpdateSpace =
    isSpaceOwner || isAdminActor || space.myAccessLevel === 'full'
  const canDeleteSpace = isSpaceOwner || isAdminActor
  // "Sharing & Permissions" opens the Share dialog to ADD / remove members and
  // change access. Only the Space owner or a platform admin (Company Admin /
  // Sub Admin with workspaces.manage) can actually do that — for everyone else
  // the dialog is read-only, so the entry point is hidden. Mirrors
  // SpaceShareDialog's `canManageAccess`, so the item appears exactly when the
  // dialog it opens will let them manage. Without this, a member who can't
  // manage sharing (e.g. a cross-department invitee) still saw the option and
  // it opened a read-only "People with access" list.
  // Prefer the server-resolved flag (covers department-scoped cross-department
  // managers too); fall back to the owner/admin heuristic if the list payload
  // predates the field.
  // A PERSONAL Space is private to its owner and can NEVER be shared — the
  // backend rejects `POST /spaces/:id/members` for one outright. Its owner is
  // also its creator, so `myCanManageMembers` is true and the option would
  // otherwise show, opening a dialog whose every action 403s. Hide it outright.
  const canManageSharing =
    !space.isPersonal &&
    (space.myCanManageMembers ??
      (isSpaceOwner || userRole === 'company_admin' || hasWorkspacesManage))
  const favorites = useFavoriteSpaces(userId)
  const hidden = useHiddenSpaces(userId)
  const isFavorite = favorites.has(space.id)
  const [actionsOpen, setActionsOpen] = useState(false)
  const [plusOpen, setPlusOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [colorOpen, setColorOpen] = useState(false)
  const [statusEditorOpen, setStatusEditorOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [listCreateOpen, setListCreateOpen] = useState(false)
  const [documentDialog, setDocumentDialog] = useState(null)
  const [folderCreateOpen, setFolderCreateOpen] = useState(false)

  const actionsTriggerRef = useRef(null)
  const actionsMenuRef = useRef(null)
  const plusTriggerRef = useRef(null)
  const plusMenuRef = useRef(null)
  /**
   * Menus open with their top aligned to the trigger's top. After mount we
   * measure the menu height and shift the top up only enough to keep the
   * bottom inside the viewport — never higher than 8px from the top edge.
   * No scrollbar; no clipping on either side.
   */
  const [actionsCoords, setActionsCoords] = useState(null)
  const [plusCoords, setPlusCoords] = useState(null)

  useLayoutEffect(() => {
    if (!actionsOpen) {
      setActionsCoords(null)
      return
    }
    const r = actionsTriggerRef.current?.getBoundingClientRect()
    if (r) setActionsCoords({ top: r.top, left: r.right + 4 })
  }, [actionsOpen])
  useLayoutEffect(() => {
    if (!actionsOpen || !actionsCoords) return
    const menu = actionsMenuRef.current
    const r = actionsTriggerRef.current?.getBoundingClientRect()
    if (!menu || !r) return
    const margin = 8
    const viewportH = window.innerHeight
    const viewportW = window.innerWidth
    let top = r.top
    if (top + menu.offsetHeight > viewportH - margin) {
      top = Math.max(margin, viewportH - margin - menu.offsetHeight)
    }
    // Flip to the LEFT of the trigger when opening right would overflow the
    // viewport (trigger near the right edge on mobile); clamp to both edges.
    let left = r.right + 4
    if (left + menu.offsetWidth > viewportW - margin) {
      left = r.left - menu.offsetWidth - 4
    }
    left = Math.max(margin, Math.min(left, viewportW - menu.offsetWidth - margin))
    if (Math.abs(top - actionsCoords.top) > 1 || Math.abs(left - actionsCoords.left) > 1) {
      setActionsCoords({ top, left })
    }
  }, [actionsOpen, actionsCoords])

  useLayoutEffect(() => {
    if (!plusOpen) {
      setPlusCoords(null)
      return
    }
    const r = plusTriggerRef.current?.getBoundingClientRect()
    if (r) setPlusCoords({ top: r.top, left: r.right + 4 })
  }, [plusOpen])
  useLayoutEffect(() => {
    if (!plusOpen || !plusCoords) return
    const menu = plusMenuRef.current
    const r = plusTriggerRef.current?.getBoundingClientRect()
    if (!menu || !r) return
    const margin = 8
    const viewportH = window.innerHeight
    const viewportW = window.innerWidth
    let top = r.top
    if (top + menu.offsetHeight > viewportH - margin) {
      top = Math.max(margin, viewportH - margin - menu.offsetHeight)
    }
    // Flip to the LEFT of the trigger when opening right would overflow the
    // viewport (trigger near the right edge on mobile); clamp to both edges.
    let left = r.right + 4
    if (left + menu.offsetWidth > viewportW - margin) {
      left = r.left - menu.offsetWidth - 4
    }
    left = Math.max(margin, Math.min(left, viewportW - menu.offsetWidth - margin))
    if (Math.abs(top - plusCoords.top) > 1 || Math.abs(left - plusCoords.left) > 1) {
      setPlusCoords({ top, left })
    }
  }, [plusOpen, plusCoords])

  // Outside click + Escape close for both menus.
  useEffect(() => {
    if (!actionsOpen && !plusOpen) return
    function onClick(e) {
      const t = e.target
      if (
        actionsOpen &&
        !actionsTriggerRef.current?.contains(t) &&
        !actionsMenuRef.current?.contains(t)
      ) {
        setActionsOpen(false)
      }
      if (
        plusOpen &&
        !plusTriggerRef.current?.contains(t) &&
        !plusMenuRef.current?.contains(t)
      ) {
        setPlusOpen(false)
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        setActionsOpen(false)
        setPlusOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [actionsOpen, plusOpen])

  // ----- mutations -----
  const renameForm = useForm({
    resolver: zodResolver(renameSchema),
    defaultValues: { name: space.name },
  })
  useEffect(() => {
    if (renameOpen) renameForm.reset({ name: space.name })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renameOpen, space.name])

  const renameMutation = useMutation({
    mutationFn: async (values) => {
      await api.patch(`/workspaces/${workspaceId}/spaces/${space.id}`, {
        name: values.name.trim(),
      })
    },
    onSuccess: () => {
      toast.success('Space renamed successfully.')
      setRenameOpen(false)
      void qc.invalidateQueries({ queryKey: ['spaces', workspaceId] })
      void qc.invalidateQueries({ queryKey: ['space', space.id] })
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      if (fields?.name) renameForm.setError('name', { message: fields.name })
      else toast.error(message)
    },
  })

  const colorMutation = useMutation({
    mutationFn: async (next) => {
      await api.patch(`/workspaces/${workspaceId}/spaces/${space.id}`, {
        color: next,
      })
    },
    onSuccess: () => {
      toast.success('Space colour updated successfully.')
      setColorOpen(false)
      void qc.invalidateQueries({ queryKey: ['spaces', workspaceId] })
      void qc.invalidateQueries({ queryKey: ['space', space.id] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const statusMutation = useMutation({
    mutationFn: async (next) => {
      await api.patch(`/workspaces/${workspaceId}/spaces/${space.id}`, {
        taskStatuses: next,
      })
    },
    onSuccess: () => {
      toast.success('Task statuses updated successfully.')
      void qc.invalidateQueries({ queryKey: ['spaces', workspaceId] })
      void qc.invalidateQueries({ queryKey: ['space', space.id] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/workspaces/${workspaceId}/spaces/${space.id}`)
    },
    onSuccess: () => {
      toast.success('Space deleted successfully.')
      setDeleteConfirm(false)
      // Drop the deleted space from local prefs so they don't linger.
      favorites.remove(space.id)
      hidden.remove(space.id)

      // If the user is currently viewing the Space they just deleted
      // (e.g. they hit the kebab → Delete from inside the Space),
      // route them somewhere live BEFORE the query invalidation kicks
      // in. Without this, `SpacePage` re-fetches a now-missing Space
      // and either lingers on stale data or flips to "Space not
      // found" — neither matches the toast saying it was deleted.
      //
      // Fallback ladder:
      //   1. The next Space in the SAME department as the one just
      //      deleted — see `pickReplacementSpace`, which owns the rule.
      //   2. Any other Space the viewer can see, when that department
      //      is now empty.
      //   3. `/app` (Dashboard) when nothing is left.
      //
      // The cached list is the API's response for THIS viewer, so it is
      // already scoped to what they may open; the helper only decides which
      // of those to land on. Taking `siblings[0]` off the raw list used to
      // drop a Company Admin into an unrelated department, because a
      // workspace spans departments and position 0 is arbitrary.
      const onDeletedSpace = location.pathname.startsWith(
        `/app/spaces/${space.id}`,
      )
      if (onDeletedSpace) {
        const cached = qc.getQueryData(['spaces', workspaceId])
        const nextId = pickReplacementSpace(cached?.data ?? [], space)
        if (nextId) {
          navigate(`/app/spaces/${nextId}`, { replace: true })
        } else {
          navigate('/app', { replace: true })
        }
      }

      void qc.invalidateQueries({ queryKey: ['spaces', workspaceId] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const duplicateMutation = useMutation({
    mutationFn: async () => {
      // POST a new space with the source's settings + " (copy)" suffix.
      const res = await api.post(`/workspaces/${workspaceId}/spaces`, {
        name: `${space.name} (copy)`.slice(0, 200),
        description: space.description ?? undefined,
        color: space.color ?? undefined,
        isPrivate: space.isPrivate,
        defaultViews: space.defaultViews,
        taskStatuses: space.taskStatuses,
      })
      return res.data
    },
    onSuccess: () => {
      toast.success('Space duplicated successfully.')
      void qc.invalidateQueries({ queryKey: ['spaces', workspaceId] })
    },
    onError: (err) => {
      // Duplicating creates a new Space, so it shares the per-workspace
      // Spaces cap. The global API interceptor already shows the single
      // amber "upgrade your plan" warning, so suppress our own error toast.
      if (isPlanLimitError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  const archiveMutation = useMutation({
    mutationFn: async () => {
      await api.patch(`/workspaces/${workspaceId}/spaces/${space.id}`, {
        isArchived: true,
      })
    },
    onSuccess: () => {
      toast.success(`"${space.name}" archived.`)
      void qc.invalidateQueries({ queryKey: ['spaces', workspaceId] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Toggle a Space between Private (owner + invitees only) and Public
  // (visible to its Department, or company-wide when it has none). Without
  // this there was no way to un-private a Space after creation, so an
  // admin-created private Space stayed invisible to members.
  const privacyMutation = useMutation({
    mutationFn: async (nextPrivate) => {
      await api.patch(`/workspaces/${workspaceId}/spaces/${space.id}`, {
        isPrivate: nextPrivate,
      })
    },
    onSuccess: (_data, nextPrivate) => {
      toast.success(
        nextPrivate
          ? 'Space set to Private.'
          : 'Space set to Public — its Department can now see it.',
      )
      void qc.invalidateQueries({ queryKey: ['spaces', workspaceId] })
      void qc.invalidateQueries({ queryKey: ['space', space.id] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  /**
   * Single dispatch for both menu paths (the "Create new" submenu inside the
   * "..." menu, and the top-level "+" menu). Keeps handler logic in one
   * place so the two menus can never drift.
   */
  function handleCreateNewPick(label) {
    switch (label) {
      case 'List':
        setListCreateOpen(true)
        return
      case 'Doc':
        setDocumentDialog('doc')
        return
      case 'Spreadsheet':
        setDocumentDialog('spreadsheet')
        return
      case 'Whiteboard':
        setDocumentDialog('whiteboard')
        return
      case 'Folder':
        setFolderCreateOpen(true)
        return
      case 'Import':
        toast.info('Import — coming soon. Doc and Spreadsheet upload will land in the next module.')
        return
      default:
        toast.info(`${label} — coming soon.`)
    }
  }

  // Copy link removed from the actions menu by request — Spaces aren't
  // shareable via a public URL, and the previous implementation just
  // copied the current `/app/spaces/:id` path which only resolved for
  // members anyway. Leaving the path scheme alone in case we want to
  // bring this back later as a "Copy share link" once the Sharing &
  // Permissions module ships.

  return (
    <>
      <div className="flex items-center gap-0.5">
        <button
          ref={actionsTriggerRef}
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setActionsOpen((v) => !v)
            setPlusOpen(false)
          }}
          aria-label="Space actions"
          title="Space actions"
          className="p-1 rounded text-[color:var(--color-muted)] hover:text-[color:var(--color-text)] hover:bg-[color:var(--color-surface-2)] cursor-pointer"
        >
          <MoreHorizontal size={14} />
        </button>
        <button
          ref={plusTriggerRef}
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setPlusOpen((v) => !v)
            setActionsOpen(false)
          }}
          aria-label="Create in space"
          title="Create"
          className="p-1 rounded text-[color:var(--color-muted)] hover:text-[color:var(--color-text)] hover:bg-[color:var(--color-surface-2)] cursor-pointer"
        >
          <Plus size={14} />
        </button>
      </div>

      {actionsOpen && actionsCoords
        ? createPortal(
            <div
              style={{
                position: 'fixed',
                top: actionsCoords.top,
                left: actionsCoords.left,
                zIndex: 60,
              }}
            >
              <ActionsMenu
                ref={actionsMenuRef}
                isFavorite={isFavorite}
                isPrivate={space.isPrivate}
                canManageWorkflow={canManageWorkflow}
                canUpdate={canUpdateSpace}
                canDelete={canDeleteSpace}
                canManageSharing={canManageSharing}
                onFavorite={() => {
                  favorites.toggle(space.id)
                  toast.success(isFavorite ? 'Removed from Favourites.' : 'Added to Favourites.')
                  setActionsOpen(false)
                }}
                onRename={() => {
                  setActionsOpen(false)
                  setRenameOpen(true)
                }}
                onTogglePrivacy={() => {
                  setActionsOpen(false)
                  privacyMutation.mutate(!space.isPrivate)
                }}
                onCreateNewItem={(label) => {
                  setActionsOpen(false)
                  handleCreateNewPick(label)
                }}
                onColor={() => {
                  setActionsOpen(false)
                  setColorOpen(true)
                }}
                onTaskStatuses={() => {
                  setActionsOpen(false)
                  navigate('/workflows')
                }}
                onDuplicate={() => {
                  setActionsOpen(false)
                  duplicateMutation.mutate()
                }}
                onArchive={() => {
                  setActionsOpen(false)
                  archiveMutation.mutate()
                }}
                onDelete={() => {
                  setActionsOpen(false)
                  setDeleteConfirm(true)
                }}
                onShare={() => {
                  setActionsOpen(false)
                  if (onShare) onShare()
                  else toast.info('Sharing & Permissions — coming soon.')
                }}
              />
            </div>,
            document.body,
          )
        : null}

      {plusOpen && plusCoords
        ? createPortal(
            <div
              style={{
                position: 'fixed',
                top: plusCoords.top,
                left: plusCoords.left,
                zIndex: 60,
              }}
            >
              <PlusMenu
                ref={plusMenuRef}
                onPick={(label) => {
                  setPlusOpen(false)
                  handleCreateNewPick(label)
                }}
                onClose={() => setPlusOpen(false)}
              />
            </div>,
            document.body,
          )
        : null}

      {/* Rename modal */}
      <Modal
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        title="Rename Space"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setRenameOpen(false)}
              disabled={renameMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={renameForm.handleSubmit((v) => renameMutation.mutate(v))}
              loading={renameMutation.isPending}
            >
              Update
            </Button>
          </>
        }
      >
        <form
          onSubmit={renameForm.handleSubmit((v) => renameMutation.mutate(v))}
        >
          <TextField
            label="Space Name"
            required
            placeholder="Enter a Space Name"
            maxLength={100}
            error={renameForm.formState.errors.name?.message}
            {...renameForm.register('name')}
          />
        </form>
      </Modal>

      {/* Colour palette modal */}
      <Modal
        open={colorOpen}
        onClose={() => setColorOpen(false)}
        title="Change Space Colour"
        size="sm"
      >
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}
        >
          {SPACE_COLOURS.map((c) => {
            const active = (space.color ?? '').toLowerCase() === c.toLowerCase()
            return (
              <button
                key={c}
                type="button"
                disabled={colorMutation.isPending}
                onClick={() => colorMutation.mutate(c)}
                aria-label={`Pick colour ${c}`}
                title={c}
                className={
                  'relative h-9 w-9 mx-auto rounded-md transition-transform cursor-pointer motion-safe:hover:scale-110 disabled:opacity-50 ' +
                  (active ? 'ring-2 ring-offset-2 ring-[color:var(--color-ink-strong)]' : '')
                }
                style={{ backgroundColor: c }}
              >
                {active ? (
                  <Check
                    size={14}
                    className="absolute inset-0 m-auto text-white drop-shadow"
                    aria-hidden="true"
                  />
                ) : null}
              </button>
            )
          })}
        </div>
      </Modal>

      {/* Task statuses editor */}
      <TaskStatusEditor
        open={statusEditorOpen}
        initial={
          space.taskStatuses && space.taskStatuses.length > 0
            ? space.taskStatuses
            : DEFAULT_SPACE_TASK_STATUSES
        }
        onClose={() => setStatusEditorOpen(false)}
        onApply={(next) => statusMutation.mutate(next)}
      />

      {/* Create List — opens the new list straight after creation so
       *  the user lands on its empty-state with the "Add Task" CTA
       *  already in view. Without this they'd stay on whatever page
       *  the dialog was launched from (typically the previously-
       *  selected list) and have to find the new entry in the sidebar
       *  manually. */}
      <ListCreateDialog
        open={listCreateOpen}
        defaultSpaceId={space.id}
        onClose={() => setListCreateOpen(false)}
        onCreated={(listId, spaceId) =>
          navigate(`/app/spaces/${spaceId}/lists/${listId}`)
        }
      />

      {/* Create Doc / Spreadsheet / Whiteboard — single dialog driven by type. */}
      <DocumentCreateDialog
        open={documentDialog !== null}
        type={documentDialog ?? 'doc'}
        defaultSpaceId={space.id}
        onClose={() => setDocumentDialog(null)}
        onCreated={(documentId) => navigate(`/app/documents/${documentId}`)}
      />

      {/* Create Project (folder entity, relabelled in the UI) */}
      <FolderCreateDialog
        open={folderCreateOpen}
        defaultSpaceId={space.id}
        onClose={() => setFolderCreateOpen(false)}
      />

      {/* Delete */}
      <ConfirmDialog
        open={deleteConfirm}
        title="Delete Space"
        message={`Are you sure you want to delete "${space.name}"? All projects, lists and tasks inside this Space will be removed. This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        confirming={deleteMutation.isPending}
        onCancel={() => setDeleteConfirm(false)}
        onConfirm={() => deleteMutation.mutate()}
      />
    </>
  )
}

// ---------- Actions menu ----------

const ActionsMenu = forwardRef(
  function ActionsMenu(props, ref) {
    const [createSubOpen, setCreateSubOpen] = useState(false)
    const createRowRef = useRef(null)

    return (
      <div
        ref={ref}
        role="menu"
        className="w-64 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)] relative"
      >
        <MenuGroup>
          <MenuItem
            icon={Star}
            label={props.isFavorite ? 'Remove from Favourites' : 'Favourite'}
            onClick={props.onFavorite}
          />
          {/* Rename + privacy are owner-only (space.update). Hidden for
           *  non-owners so the form can't be opened at all. */}
          {props.canUpdate ? (
            <>
              <MenuItem icon={Pencil} label="Rename" onClick={props.onRename} />
              <MenuItem
                icon={props.isPrivate ? Users : Lock}
                label={props.isPrivate ? 'Make Public' : 'Make Private'}
                onClick={props.onTogglePrivacy}
              />
            </>
          ) : null}
        </MenuGroup>
        <Divider />
        <MenuGroup>
          <div
            ref={createRowRef}
            onMouseEnter={() => setCreateSubOpen(true)}
            onMouseLeave={() => setCreateSubOpen(false)}
            className="relative"
          >
            <MenuItem
              icon={PlusCircle}
              label="Create new"
              hasSubmenu
              active={createSubOpen}
              onClick={() => setCreateSubOpen((v) => !v)}
            />
            {createSubOpen ? (
              <div
                role="menu"
                className="absolute left-full top-0 ml-1 w-56 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)]"
              >
                {/* Doc / Spreadsheet / Whiteboard / Import options
                 *  were removed from this submenu by request. The
                 *  corresponding handler branches stay in
                 *  `handleCreateNewPick` so the feature can be
                 *  re-enabled by just bringing the MenuItems back. */}
                <MenuGroup>
                  <MenuItem
                    icon={ListChecks}
                    label="List"
                    onClick={() => props.onCreateNewItem('List')}
                  />
                  <MenuItem
                    icon={Folder}
                    label="Project"
                    onClick={() => props.onCreateNewItem('Folder')}
                  />
                </MenuGroup>
              </div>
            ) : null}
          </div>
          {props.canUpdate ? (
            <MenuItem icon={Palette} label="Colour & Icon" hasSubmenu onClick={props.onColor} />
          ) : null}
          {props.canManageWorkflow ? (
            <MenuItem
              icon={ListChecks}
              label="Task statuses"
              onClick={props.onTaskStatuses}
            />
          ) : null}
        </MenuGroup>
        <Divider />
        <MenuGroup>
          <MenuItem icon={Copy} label="Duplicate" onClick={props.onDuplicate} />
          {/* Archive (space.update) + Delete (owner/admin only) are owner-only
           *  — hidden for non-owners. */}
          {props.canUpdate ? (
            <MenuItem icon={Archive} label="Archive" onClick={props.onArchive} />
          ) : null}
          {props.canDelete ? (
            <MenuItem icon={Trash2} label="Delete" danger onClick={props.onDelete} />
          ) : null}
        </MenuGroup>
        {/* Sharing & Permissions — only for those who can actually manage the
         *  Space's access (owner / platform admin). Hidden for members who
         *  would only get a read-only dialog. */}
        {props.canManageSharing ? (
          <div className="px-2 py-2 bg-[color:var(--color-canvas)]">
            <button
              type="button"
              onClick={props.onShare}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold text-[color:var(--color-ink-strong)] bg-[color:var(--color-card)] border border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              <Share2 size={14} />
              Sharing &amp; Permissions
            </button>
          </div>
        ) : null}
      </div>
    )
  },
)

// ---------- "+" Plus menu ----------

const PlusMenu = forwardRef(
  function PlusMenu(props, ref) {
    void props.onClose
    return (
      <div
        ref={ref}
        role="menu"
        className="w-60 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)] overflow-hidden"
      >
        <p className="px-4 pt-3 pb-1 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
          Create
        </p>
        <MenuGroup>
          <MenuItem
            icon={ListChecks}
            label="List"
            description="A board of Tasks (e.g. one List per SOP/phase)"
            onClick={() => props.onPick('List')}
          />
          <MenuItem
            icon={Folder}
            label="Project"
            description="Group Lists & Docs"
            onClick={() => props.onPick('Folder')}
          />
        </MenuGroup>
        {/* Doc / Spreadsheet / Whiteboard / Import sections were
         *  removed from this menu by request. The handler in
         *  `handleCreateNewPick` retains the branches so the items
         *  can be brought back by re-adding the MenuItems here. */}
      </div>
    )
  },
)

// ---------- shared menu primitives ----------
function MenuGroup({ children }) {
  return <div className="py-1">{children}</div>
}

function Divider() {
  return <div className="border-t border-[color:var(--color-rule)]" aria-hidden="true" />
}

function MenuItem({ icon: Icon, label, description, hasSubmenu, danger, active, onClick }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={
        'w-full flex items-start gap-3 px-3 py-2 text-sm text-left cursor-pointer ' +
        (active ? 'bg-[color:var(--color-card-hover)] ' : 'hover:bg-[color:var(--color-card-hover)] ') +
        (danger ? 'text-[color:var(--color-priority-urgent)]' : 'text-[color:var(--color-ink)]')
      }
    >
      <Icon
        size={14}
        className={
          'mt-0.5 shrink-0 ' +
          (danger ? 'text-[color:var(--color-priority-urgent)]' : 'text-[color:var(--color-ink-muted)]')
        }
      />
      <span className="flex-1 min-w-0">
        <span className="block font-medium">{label}</span>
        {description ? (
          <span className="block text-xs text-[color:var(--color-ink-muted)] mt-0.5">
            {description}
          </span>
        ) : null}
      </span>
      {hasSubmenu ? (
        <ChevronRight size={14} className="text-[color:var(--color-ink-muted)] mt-0.5" aria-hidden="true" />
      ) : null}
    </button>
  )
}
